import type { SupabaseClient } from "@supabase/supabase-js";

import { humanizeLabel } from "../data/alert-queue";
import { fetchMyShifts, type MyShiftRow } from "../data/schedule";
import { useReadModel } from "../data/useReadModel";
import { formatDisplayTime } from "../lib/display-time";

// "Guard-visible shift shape" preview - public.my_shifts returns ONLY the signed-in user's
// assignments (auth.uid() server-side), so for most console operators this is the honest empty
// state. That emptiness is deliberate: it documents the exact shape guards see on mobile/web
// without pretending the console can browse anyone else's personal schedule. Declined renders
// muted (the guard's own history), never as an error - design-doc tone doctrine.

function responseCopy(row: MyShiftRow): string {
  if (row.responseStatus === "acknowledged") {
    return "Confirmed";
  }
  if (row.responseStatus === "declined") {
    return row.declineReason ? `Declined - ${humanizeLabel(row.declineReason)}` : "Declined";
  }
  return "Awaiting confirm";
}

function responseTone(row: MyShiftRow): "acknowledged" | "declined" | "none" {
  if (row.responseStatus === "acknowledged" || row.responseStatus === "declined") {
    return row.responseStatus;
  }
  return "none";
}

export function MyShiftsPreview({
  client,
  accountId,
  timeZone,
}: {
  client: SupabaseClient;
  accountId: string;
  timeZone: string;
}) {
  const { status, data, error } = useReadModel<MyShiftRow[]>(
    () => fetchMyShifts(client, accountId, new Date().toISOString()),
    [client, accountId],
  );

  if (status === "loading" && data === null) {
    return <p className="surface__state" role="status">Loading your shifts...</p>;
  }
  if (status === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {error ?? "Could not load your shifts."}
      </p>
    );
  }

  const rows = data ?? [];
  if (rows.length === 0) {
    return (
      <p className="surface__state" role="status">
        You have no shift assignments - this view shows only your own. Guards see their upcoming
        shifts in exactly this shape on mobile and Guard Web.
      </p>
    );
  }

  return (
    <ul className="myshifts" aria-label="Your shifts">
      {rows.map((row) => (
        <li
          key={row.routeAssignmentId}
          className={`myshifts__item${row.responseStatus === "declined" ? " myshifts__item--declined" : ""}`}
        >
          <span className="myshifts__where">
            {row.siteName ?? "-"}
            {row.routeName ? ` - ${row.routeName}` : ""}
          </span>
          <span className="myshifts__when">
            {row.startsAt ? formatDisplayTime(row.startsAt, timeZone) : "-"}
            {" to "}
            {row.endsAt ? formatDisplayTime(row.endsAt, timeZone) : "-"}
          </span>
          <span className={`myshifts__response myshifts__response--${responseTone(row)}`}>
            {responseCopy(row)}
          </span>
        </li>
      ))}
    </ul>
  );
}
